import { useEffect, useState, useCallback, useRef } from 'react';
import { Capacitor } from '@capacitor/core';
import { registerDeviceToken, unregisterDeviceToken } from '../services/pushNotifications';

/**
 * Hook for managing push notifications on iOS/Android
 * 
 * This hook:
 * - Requests push notification permission
 * - Registers with APNs/FCM
 * - Sends device token to backend
 * - Handles notification received/tapped events
 */

interface UsePushNotificationsOptions {
  enabled?: boolean;
  onNotificationReceived?: (notification: PushNotificationData) => void;
  onNotificationTapped?: (notification: PushNotificationData) => void;
}

interface PushNotificationData {
  title?: string;
  body?: string;
  data?: Record<string, unknown>;
}

interface UsePushNotificationsReturn {
  isSupported: boolean;
  isRegistered: boolean;
  permissionStatus: 'prompt' | 'granted' | 'denied' | 'unknown';
  register: () => Promise<void>;
  unregister: () => Promise<void>;
}

export const usePushNotifications = (
  options: UsePushNotificationsOptions = {}
): UsePushNotificationsReturn => {
  const { enabled = true, onNotificationReceived, onNotificationTapped } = options;
  const [isSupported] = useState(Capacitor.isNativePlatform());
  const [isRegistered, setIsRegistered] = useState(false);
  const [permissionStatus, setPermissionStatus] = useState<'prompt' | 'granted' | 'denied' | 'unknown'>('unknown');
  const tokenRef = useRef<string | null>(null);
  const pluginRef = useRef<typeof import('@capacitor/push-notifications').PushNotifications | null>(null);

  // Initialize the plugin
  useEffect(() => {
    if (!isSupported) return;

    let isMounted = true;

    const initPlugin = async () => {
      try {
        const module = await import('@capacitor/push-notifications');
        if (!isMounted) return;

        pluginRef.current = module.PushNotifications;

        // Check current permission status
        const permStatus = await pluginRef.current.checkPermissions();
        if (isMounted) {
          setPermissionStatus(permStatus.receive as 'prompt' | 'granted' | 'denied');
        }
      } catch (error) {
        if (isMounted) {
          console.warn('Failed to initialize push notifications:', error);
        }
      }
    };

    initPlugin();

    return () => {
      isMounted = false;
    };
  }, [isSupported]);

  // Set up event listeners
  useEffect(() => {
    if (!isSupported || !pluginRef.current || !enabled) return;

    const plugin = pluginRef.current;
    const listeners: Array<{ remove: () => Promise<void> }> = [];
    let isMounted = true;

    const setupListeners = async () => {
      // Registration success
      const regListener = await plugin.addListener('registration', async (token) => {
        // Push notification token received
        tokenRef.current = token.value;

        // Send token to backend
        try {
          const platform = Capacitor.getPlatform() as 'ios' | 'android';
          await registerDeviceToken(token.value, platform);
          if (isMounted) {
            setIsRegistered(true);
          }
        } catch (error) {
          // Failed to register device token with backend
        }
      });
      listeners.push(regListener);

      // Registration error
      const regErrorListener = await plugin.addListener('registrationError', (_error) => {
        // Push notification registration error
      });
      listeners.push(regErrorListener);

      // Notification received (app in foreground)
      const receivedListener = await plugin.addListener('pushNotificationReceived', (notification) => {
        // Push notification received
        if (isMounted) {
          onNotificationReceived?.({
            title: notification.title,
            body: notification.body,
            data: notification.data,
          });
        }
      });
      listeners.push(receivedListener);

      // Notification tapped
      const actionListener = await plugin.addListener('pushNotificationActionPerformed', (action) => {
        // Push notification action performed
        if (isMounted) {
          onNotificationTapped?.({
            title: action.notification.title,
            body: action.notification.body,
            data: action.notification.data,
          });
        }
      });
      listeners.push(actionListener);
    };

    setupListeners();

    return () => {
      isMounted = false;
      listeners.forEach((listener) => listener.remove());
    };
  }, [isSupported, enabled, onNotificationReceived, onNotificationTapped]);

  // Register for push notifications
  const register = useCallback(async () => {
    if (!pluginRef.current) return;

    try {
      let permStatus = await pluginRef.current.checkPermissions();
      
      if (permStatus.receive === 'prompt') {
        permStatus = await pluginRef.current.requestPermissions();
      }
      
      setPermissionStatus(permStatus.receive as 'prompt' | 'granted' | 'denied');
      
      if (permStatus.receive === 'granted') {
        await pluginRef.current.register();
      }
    } catch (error) {
      console.error('Failed to register for push notifications:', error);
    }
  }, []);

  // Unregister from push notifications
  const unregister = useCallback(async () => {
    if (!pluginRef.current) return;

    try {
      await pluginRef.current.unregister();
      
      if (tokenRef.current) {
        await unregisterDeviceToken(tokenRef.current);
        tokenRef.current = null;
      }
      
      setIsRegistered(false);
    } catch (error) {
      console.error('Failed to unregister from push notifications:', error);
    }
  }, []);

  return {
    isSupported,
    isRegistered,
    permissionStatus,
    register,
    unregister,
  };
};

export default usePushNotifications;

